import { useEffect, useRef } from "react";
import mixpanel from "mixpanel-browser";
import mixpanelTrackingDefinitions from "../../../../packages/common/config/mixpanelTrackingDefinations.json";

/**
 * Hook to track when the user closes the webpage/application
 * Uses visibilitychange and pagehide events for reliable tracking
 */
export function usePageCloseTracking() {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const handlePageClose = () => {
      // Prevent duplicate tracking
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const eventData =
        mixpanelTrackingDefinitions.mixpanelEvents["webpageClose"];

      if (eventData) {
        try {
          // Use mixpanel's track method which internally handles sendBeacon for unload events
          mixpanel.track(eventData.EventName, {
            PageName: eventData.PageName,
            EventDetail: eventData.EventDetail,
          });
        } catch (error) {
          console.error("Error tracking webpage close:", error);
        }
      }
    };

    const handleVisibilityChange = () => {
      // Track when page becomes hidden (tab closed, minimized, switched)
      if (document.visibilityState === "hidden") {
        handlePageClose();
      }
    };

    // visibilitychange: Most reliable for detecting when user leaves the page
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // pagehide: Backup for when page is unloaded (especially mobile Safari)
    window.addEventListener("pagehide", handlePageClose);

    // Cleanup event listeners on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageClose);
    };
  }, []);
}
